import {
  RemoteConfigKey,
  RemoteConfigValues,
} from '@/config/firebase/remoteConfig';
import {
  fetchRemoteConfig,
  getConfig,
  subscribeToConfigUpdates,
} from '@/utils/remoteConfig';
import { useCallback, useEffect, useState } from 'react';

interface UseRemoteConfigResult {
  isLoading: boolean;
  isReady: boolean;
  error: Error | null;
  getConfig: <K extends RemoteConfigKey>(
    key: K,
    defaultValue?: RemoteConfigValues[K]
  ) => RemoteConfigValues[K];
  refetch: () => Promise<void>;
}

export const useRemoteConfig = ({
  listenForUpdates = true,
}: {
  listenForUpdates?: boolean;
} = {}): UseRemoteConfigResult => {
  const [isLoading, setIsLoading] = useState(true);
  const [isReady, setIsReady] = useState(false);
  const [error, setError] = useState<Error | null>(null);

  const fetchConfig = useCallback(async () => {
    setIsLoading(true);
    setError(null);

    try {
      const success = await fetchRemoteConfig();
      setIsReady(success);
    } catch (err) {
      setError(err instanceof Error ? err : new Error('Unknown error'));
      setIsReady(false);
    } finally {
      setIsLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchConfig();
  }, [fetchConfig]);

  useEffect(() => {
    if (!listenForUpdates) return;

    const unsubscribe = subscribeToConfigUpdates(() => {
      fetchConfig();
    });

    return unsubscribe;
  }, [listenForUpdates, fetchConfig]);

  return {
    isLoading,
    isReady,
    error,
    getConfig,
    refetch: fetchConfig,
  };
};
